// Operator › Break-glass (`/operator/break-glass`; M5-05, 05 §Agency cross-company access):
// time-boxed elevated access into one company. An agency user asks (company, scope, up to 4 h,
// a reason); a second agency admin (or the company's owner, on the company side) approves or
// rejects; the requester or an admin ends a live session early. One call on load
// (`GET /v1/operator/break-glass`); the API's problems (403 not an admin, 409 already decided)
// arrive as toasts and inline.
import { newIdempotencyKey } from "@companyos/sdk";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  EmptyState,
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  Input,
  LocationHeader,
  NativeSelect,
  NativeSelectOption,
  Segmented,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  Toolbar,
  toast,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { KeyRoundIcon, ShieldAlertIcon } from "lucide-react";
import { type FormEvent, useId, useState } from "react";
import type { Api } from "../../api/client";
import type { MeResponse } from "../../api/types";
import { problemText } from "../ops-parts";
import { OpsBody, OpsFrame, operatorCrumb } from "../ops-ux";
import { LoadError, Loading } from "../screen-parts";
import {
  DecisionText,
  durationText,
  RejectDialog,
  SessionStateBadge,
  SessionTimes,
  scopeText,
} from "./agency-parts";
import { type BreakGlassSession, operatorBreakGlassQuery } from "./agency-queries";

const DURATIONS = [15, 30, 60, 120, 240] as const;
const SCOPES = [
  { value: "company", label: "Whole company" },
  { value: "space", label: "One space" },
  { value: "channel", label: "One channel" },
] as const;
type Scope = (typeof SCOPES)[number]["value"];
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function BreakGlassPage({ api, me }: { api: Api; me: MeResponse }) {
  const q = useQuery(operatorBreakGlassQuery(api));
  const rows = q.data?.data ?? [];
  const companyName = (id: string) =>
    me.agency?.grants.find((g) => g.company_id === id)?.company_name ?? `…${id.slice(-6)}`;
  const [filter, setFilter] = useState<Filter>("all");
  const shown = rows.filter((s) => matches(s, filter));
  const live = rows.filter((s) => s.state === "active" || s.state === "requested").length;
  return (
    <OpsFrame>
      <LocationHeader
        crumbs={[operatorCrumb, { label: "Break-glass" }]}
        icon={ShieldAlertIcon}
        title="Break-glass"
        count={q.isPending ? undefined : live}
        actions={<RequestDialog api={api} me={me} />}
      />
      <Toolbar bleed>
        <Segmented
          size="sm"
          aria-label="State"
          value={filter}
          onValueChange={setFilter}
          options={FILTERS.map((f) => ({ value: f, label: FILTER_LABEL[f] }))}
        />
        <span className="ml-auto shrink-0 text-xs text-muted-foreground">
          Up to 4 h, approved by a second admin or the company's owner, audited.
        </span>
      </Toolbar>
      <OpsBody>
        {q.isPending ? (
          <Loading label="Loading break-glass sessions" rows={4} />
        ) : q.isError ? (
          <LoadError what="Break-glass sessions" error={q.error} />
        ) : rows.length === 0 ? (
          <EmptyState
            variant="card"
            icon={ShieldAlertIcon}
            title="No break-glass sessions"
            description="Requests for elevated access, and their approvals, show up here."
          />
        ) : (
          <Table data-slot="break-glass">
            <TableCaption>Newest first. Every session is in the company's audit log.</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead scope="col">Session</TableHead>
                <TableHead scope="col">Requested by</TableHead>
                <TableHead scope="col">Reason</TableHead>
                <TableHead scope="col">When</TableHead>
                <TableHead scope="col">Decision</TableHead>
                <TableHead scope="col" className="text-right">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.map((s) => (
                <SessionRow
                  key={s.id}
                  api={api}
                  me={me}
                  s={s}
                  company={companyName(s.company_id)}
                />
              ))}
            </TableBody>
          </Table>
        )}
      </OpsBody>
    </OpsFrame>
  );
}

const FILTERS = ["all", "requested", "active", "closed"] as const;
type Filter = (typeof FILTERS)[number];
const FILTER_LABEL: Record<Filter, string> = {
  all: "All",
  requested: "Requested",
  active: "Active",
  closed: "Closed",
};
const matches = (s: BreakGlassSession, f: Filter) =>
  f === "all" ||
  (f === "closed"
    ? s.state === "rejected" || s.state === "ended" || s.state === "expired"
    : s.state === f);

function SessionRow({
  api,
  me,
  s,
  company,
}: {
  api: Api;
  me: MeResponse;
  s: BreakGlassSession;
  company: string;
}) {
  const qc = useQueryClient();
  const [rejecting, setRejecting] = useState(false);
  const refresh = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ["operator", "break-glass"] }),
      qc.invalidateQueries({ queryKey: ["company", s.company_id, "break-glass"] }),
    ]);
  const admin = me.agency?.role === "admin";
  const mine = s.requested_by_user_id === me.user.id;
  const approve = useMutation({
    mutationFn: () =>
      api.client.operator.breakGlass.approve({
        params: { session_id: s.id },
        idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: async () => {
      toast.add({ title: `Break-glass approved for ${s.requested_by_name}`, type: "success" });
      await refresh();
    },
  });
  const reject = useMutation({
    mutationFn: (reason: string) =>
      api.client.operator.breakGlass.reject({
        params: { session_id: s.id },
        idempotencyKey: newIdempotencyKey(),
        body: { reason },
      }),
    onSuccess: async () => {
      setRejecting(false);
      await refresh();
    },
  });
  const end = useMutation({
    mutationFn: () =>
      api.client.operator.breakGlass.end({
        params: { session_id: s.id },
        idempotencyKey: newIdempotencyKey(),
        body: {},
      }),
    onSuccess: async () => {
      toast.add({ title: "Break-glass session ended", type: "success" });
      await refresh();
    },
  });
  const decides = s.state === "requested" && admin && !mine;
  const ends = s.state === "active" && (mine || admin);
  const error = approve.error ?? reject.error ?? end.error;
  return (
    <TableRow data-session={s.id} data-session-state={s.state}>
      <TableHead scope="row" className="font-normal">
        <div className="flex flex-col items-start gap-1">
          <SessionStateBadge state={s.state} />
          <span className="font-medium">{company}</span>
          <span className="text-xs text-muted-foreground">
            {scopeText(s)} · {durationText(s.duration_minutes)}
          </span>
        </div>
      </TableHead>
      <TableCell>
        {s.requested_by_name}
        {mine ? <span className="text-muted-foreground"> (you)</span> : null}
      </TableCell>
      <TableCell className="max-w-72 text-sm whitespace-normal">{s.reason}</TableCell>
      <TableCell>
        <SessionTimes s={s} />
      </TableCell>
      <TableCell className="whitespace-normal">
        <DecisionText s={s} />
      </TableCell>
      <TableCell className="text-right">
        <div className="flex flex-wrap justify-end gap-2">
          {decides ? (
            <>
              <Button size="sm" disabled={approve.isPending} onClick={() => approve.mutate()}>
                Approve
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setRejecting(true)}>
                Reject
              </Button>
            </>
          ) : null}
          {ends ? (
            <Button
              size="sm"
              variant="destructive"
              disabled={end.isPending}
              onClick={() => end.mutate()}
            >
              {end.isPending ? "Ending…" : "End now"}
            </Button>
          ) : null}
        </div>
        {error ? (
          <p className="mt-1 text-xs text-danger-text" role="alert">
            {problemText(error, "That did not work.")}
          </p>
        ) : null}
        <RejectDialog
          open={rejecting}
          onOpenChange={setRejecting}
          who={s.requested_by_name}
          pending={reject.isPending}
          onReject={(reason) => reject.mutate(reason)}
        />
      </TableCell>
    </TableRow>
  );
}

function RequestDialog({ api, me }: { api: Api; me: MeResponse }) {
  const qc = useQueryClient();
  const companies = [...(me.agency?.grants ?? [])]
    .filter((g) => g.level === "operator")
    .sort((a, b) => a.company_name.localeCompare(b.company_name));
  const [open, setOpen] = useState(false);
  const [company, setCompany] = useState(companies[0]?.company_id ?? "");
  const [scope, setScope] = useState<Scope>("company");
  const [scopeId, setScopeId] = useState("");
  const [duration, setDuration] = useState<number>(60);
  const [reason, setReason] = useState("");
  const ids = {
    company: useId(),
    scope: useId(),
    scopeId: useId(),
    duration: useId(),
    reason: useId(),
  };
  const request = useMutation({
    mutationFn: () =>
      api.client.operator.breakGlass.request({
        idempotencyKey: newIdempotencyKey(),
        body: {
          company_id: company,
          reason: reason.trim(),
          duration_minutes: duration,
          scope,
          ...(scope === "company" ? {} : { scope_id: scopeId.trim() }),
        },
      }),
    onSuccess: async (s) => {
      toast.add({
        title: "Break-glass requested",
        description: "A second agency admin or the company's owner decides.",
        type: "success",
      });
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["operator", "break-glass"] }),
        qc.invalidateQueries({ queryKey: ["company", s.company_id, "break-glass"] }),
      ]);
      setOpen(false);
      setReason("");
      setScope("company");
      setScopeId("");
    },
  });
  const reasonOk = reason.trim().length >= 10;
  const scopeOk = scope === "company" || UUID.test(scopeId.trim());
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (company && reasonOk && scopeOk) request.mutate();
  };
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) request.reset();
      }}
    >
      <DialogTrigger render={<Button size="sm" disabled={companies.length === 0} />}>
        <KeyRoundIcon aria-hidden="true" />
        Request access
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={submit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>Request break-glass access</DialogTitle>
            <DialogDescription>
              Elevated access for up to 4 hours. Another agency admin or the company's owner must
              approve it; the company sees who, why and until when.
            </DialogDescription>
          </DialogHeader>
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor={ids.company}>Company</FieldLabel>
              <NativeSelect
                id={ids.company}
                value={company}
                onChange={(e) => setCompany(e.target.value)}
              >
                {companies.map((g) => (
                  <NativeSelectOption key={g.company_id} value={g.company_id}>
                    {g.company_name}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </Field>
            <Field>
              <FieldLabel htmlFor={ids.scope}>Scope</FieldLabel>
              <NativeSelect
                id={ids.scope}
                value={scope}
                onChange={(e) => setScope(e.target.value as Scope)}
              >
                {SCOPES.map((s) => (
                  <NativeSelectOption key={s.value} value={s.value}>
                    {s.label}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </Field>
            {scope !== "company" ? (
              <Field>
                <FieldLabel htmlFor={ids.scopeId}>
                  {scope === "space" ? "Space id" : "Channel id"}
                </FieldLabel>
                <Input
                  id={ids.scopeId}
                  required
                  value={scopeId}
                  onChange={(e) => setScopeId(e.target.value)}
                  placeholder="0193…"
                />
                <FieldDescription>The id from the {scope}'s URL.</FieldDescription>
              </Field>
            ) : null}
            <Field>
              <FieldLabel htmlFor={ids.duration}>Duration</FieldLabel>
              <NativeSelect
                id={ids.duration}
                value={String(duration)}
                onChange={(e) => setDuration(Number(e.target.value))}
              >
                {DURATIONS.map((d) => (
                  <NativeSelectOption key={d} value={String(d)}>
                    {durationText(d)}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </Field>
            <Field>
              <FieldLabel htmlFor={ids.reason}>Reason</FieldLabel>
              <Textarea
                id={ids.reason}
                rows={3}
                minLength={10}
                maxLength={2000}
                required
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
              <FieldDescription>At least 10 characters; the company sees it.</FieldDescription>
            </Field>
            {request.isError ? (
              <FieldError>
                {problemText(request.error, "The request could not be made.")}
              </FieldError>
            ) : null}
          </FieldGroup>
          <DialogFooter>
            <Button type="submit" disabled={request.isPending || !reasonOk || !scopeOk || !company}>
              {request.isPending ? "Requesting…" : "Request access"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
